import _ from 'lodash';

/**
 * Given the list of the responses of a survey and the involved service configurations, returns a list of sets of
 * responses obtained by exploding all recursive responses (if the survey doesn't contain recursive responses, this
 * function will be a passthrough).
 * Say that we have a survey S with two recursive questions A and B, such that A has three responses (a1, a2, a3) and B
 * has two (b1, b2); S also has a list X of non-recursive responses. This function will return the following result:
 * [{...X, a1, b1}, {...X, a1, b2}, {...X, a2, b1}, {...X, a2, b2}, {...X, a3, b1}, {...X, a3, b2}].
 * This can be particularly helpful if we need to explore all possible combinations of responses (e.g. in order to
 * establish if a particular dependency has been satisfied).
 *
 * @param responses a list of responses
 * @param configurations a list of service configurations
 * @param evaluateValues if true, the values of the responses will be evaluated
 * @returns a list of lists of responses (one for each exploded recursion)
 */
const explodeSurveyResponses = (responses, configurations, evaluateValues=true, computeCartesiaProduct=true) => {

  const recursiveQuestionsIds = configurations
    .filter((c) => c.type === 'recursive_question')
    .map((c) => c.id);

  let {immediate, recursive} = responses.reduce(
    (prev, curr) => {
      const key = recursiveQuestionsIds.includes(curr.configuration_input_id) ? 'recursive' : 'immediate';
      const toAdd = key === "recursive" ? curr.recursive_question_value : curr;
      return {...prev, [key]: [...prev[key], toAdd]};
    },
    {immediate: [], recursive: []}
  )

  // enrich immediate responses with additional data in order to evaluate dependencies
  const transformedImmediateResponses = immediate.reduce(
    (prev, curr) => ({
      ...prev,
      [curr.configuration_input_id]: {
        ...curr,
        value: evaluateValues ? eval(curr.value) : curr.value,  // eslint-disable-line
        question: configurations.find((c) => c.id === curr.configuration_input_id)
      }
    }),
    {}
  );

  // if the survey contains recursive responses, generate many "virtual" surveys containing every combination
  // of recursive questions rows like they were immediate-like answers. this way we can provide the dependencies
  // evaluation function a list of simple surveys: if at least one matches the dependencies, our original survey
  // can be counted as valid
  const cartesianProduct = (...arrays) => {
    if (arrays.length === 0)
      return [[]];

    const [head, ...tail] = arrays;
    const tailCartesianProduct = cartesianProduct(...tail);
    const result = [];

    for (const h of head)
      for (const t of tailCartesianProduct)
        result.push([h, ...t]);

    return result;
  }
  /*
  HUBSPOT TICKET https://app.hubspot.com/contacts/8760420/record/0-5/19258367603/
  Because of a bug in passing from design survey to quotation survey there were empty
  arrays of recursive responses. Those empty arrays maded the cartesianProduct function
  return an empty array (we were losing some recursive responses because of that).
  This somehow led to no analisi prezzi being matched and returned.
  Passing from design to quotation has been fixed but for retrocompatibility we need to
  filter out empty recursive responses arrays.
  */
  recursive = recursive.filter((r) => r.length > 0);
  /*
  HUBSPOT TICKET https://app.hubspot.com/contacts/8760420/record/0-5/35530345218
  no cartesian product should be computed when building the payload.
  this led to duplicated analisi prezzi in the xpwe generated.
  In a rinforzo strutturale survey with 2 recursive questions (nodi di facciata and nodi d'angolo),
  each with 2 rows, the cartesian product generated 4 sets of responses
  {nodi di facciata 1, nodi d'angolo 1}
  {nodi di facciata 1, nodi d'angolo 2}
  {nodi di facciata 2, nodi d'angolo 1}
  {nodi di facciata 2, nodi d'angolo 2}
  the two analisi prezzi were duplicated because their dependencies were satisfied by
  each of the 4 sets of responses. To avoid this, when building the payload,
  we just need to flatten the recursive responses instead of computing the cartesian product.
   */
  const recursiveCombinations = recursive.length > 0 ? computeCartesiaProduct ? cartesianProduct(...recursive) : _.flatMap(recursive).map((el) => [el]) : null;

  const containsRecursive = recursiveCombinations !== null
  const groups = !containsRecursive
    ? [transformedImmediateResponses]
    : recursiveCombinations.map((rc) => {
      const merged = rc.reduce((acc, curr) => Object.assign(acc, curr), {});
      return {...transformedImmediateResponses, ...merged};
    });

  return {groups, containsRecursive};
}

export default explodeSurveyResponses;